import { useSearchParams, useNavigate } from "react-router-dom";

import { APP_ROUTES } from "const";
import { useProfileCompleteness } from "hooks";

/**
 * Larger, warmer welcome shown on the profile page right after signup.
 * Activated by the ?welcome=true query param set during the OTP flow.
 */
export function ProfileWelcomeBanner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, isComplete, missingLabels } =
    useProfileCompleteness();

  const welcome = searchParams.get("welcome") === "true";

  if (!welcome || !isAuthenticated || isLoading) {
    return null;
  }

  const handleSkip = () => {
    // Remove the query param so it doesn't reappear on navigation back
    searchParams.delete("welcome");
    setSearchParams(searchParams, { replace: true });
    navigate(APP_ROUTES.ISSUES);
  };

  const handleDismiss = () => {
    searchParams.delete("welcome");
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div
      className="mx-2 mt-2 mb-4 rounded-lg border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-5 shadow-sm"
      role="region"
      aria-label="Welcome"
    >
      <h2 className="text-lg font-semibold text-stone-900">
        Welcome to WEattitude!
      </h2>
      <p className="mt-2 text-sm text-stone-700 leading-relaxed">
        {isComplete ? (
          <>
            Your profile is all set. You can tweak anything below, or head
            straight to the action.
          </>
        ) : (
          <>
            Take a minute to fill in{" "}
            <span className="font-semibold">{missingLabels.join(", ")}</span>{" "}
            below. This helps other members recognise who you are and lets
            WEattitude surface issues, solutions and action groups you'll
            actually care about.
          </>
        )}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {isComplete ? (
          <button
            type="button"
            onClick={handleDismiss}
            className="text-sm font-semibold text-orange-700 hover:text-orange-900 underline"
          >
            Got it
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSkip}
            className="text-sm font-medium text-stone-600 hover:text-stone-900 underline"
          >
            Skip for now
          </button>
        )}
      </div>
    </div>
  );
}
